import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useItemPrice } from "../config";
import { APIProvider, Map as GoogleMap } from "@vis.gl/react-google-maps";
import type { DiscoverItem } from "../types";
import {
  API_KEY,
  CITY_ZOOM,
  COUNTRY_ZOOM,
  MAP_ID,
  MOROCCO_CENTER,
  VIEWPORT_SYNC_ZOOM,
  cityCentroids,
  nearestCitySlug,
} from "./map/brand-map";
import { BoundsReporter } from "./map/bounds-reporter";
import type { ViewportBounds } from "../state/discover-reducer";
import { styleForItem } from "./map/category-style";
import { thumbUrl } from "./map/thumb-url";
import {
  ClusteredPins,
  FitBoundsController,
  MapClickCloser,
} from "./map/clustered-pins";
import { useDiscoverActions, useDiscoverItems } from "../state/discover-context";

interface Props {
  /** Currently-filtered places to plot as clustered pins. */
  readonly items: readonly DiscoverItem[];
  /** Active city slug (drives the framing); null = country overview. */
  readonly activeCitySlug: string | null;
  /** Open a place's detail when its mini-card "View" is tapped. */
  readonly onSelectItem: (id: string) => void;
  /** Publish the map viewport (debounced) so the sheet can narrow to it. */
  readonly onBoundsChange?: (bounds: ViewportBounds | null) => void;
}

/**
 * The row of cards a tapped cluster bubble opens into. Anchored where the
 * single-pin card sits, over the map rather than inside the sheet: the cards
 * are the answer to "what is here", so covering the map with them would be
 * answering a question about a place by hiding it. Vertical padding is room
 * for the cards' own shadow, which a scrolling row would otherwise cut off.
 */
const CLUSTER_ROW_STYLE: CSSProperties = {
  position: "fixed",
  left: 0,
  right: 0,
  bottom: "calc(100dvh - var(--mj-sheet-top, 62dvh) - 8px)",
  zIndex: 60,
  display: "flex",
  gap: "10px",
  padding: "16px 7vw 20px",
  overflowX: "auto",
  scrollSnapType: "x mandatory",
  scrollbarWidth: "none",
};

/** Undoes the single card's fixed centring so it can sit inside the row. */
const CLUSTER_CARD_STYLE: CSSProperties = {
  position: "static",
  left: "auto",
  bottom: "auto",
  transform: "none",
  flex: "0 0 auto",
  scrollSnapAlign: "center",
};

/**
 * Full-bleed branded Google Map shown behind the mobile Discover sheet. Tapping
 * a pin raises a floating mini-card (option B) above the sheet; "View" opens the
 * detail. The card is portaled to <body> so it stacks above the sheet regardless
 * of the backdrop's z-index.
 */
export function DiscoverMobileMapBackdrop({
  items,
  activeCitySlug,
  onSelectItem,
  onBoundsChange,
}: Props) {
  const { t, i18n } = useTranslation();
  const itemPrice = useItemPrice();
  const allItems = useDiscoverItems();
  const { setCity } = useDiscoverActions();
  const [floatingId, setFloatingId] = useState<string | null>(null);
  // The venues behind a tapped bubble that no zoom would ever break apart.
  const [clusterIds, setClusterIds] = useState<readonly string[] | null>(null);
  const floatingItem =
    floatingId !== null ? items.find((i) => i.id === floatingId) ?? null : null;
  const clusterItems = useMemo<readonly DiscoverItem[]>(() => {
    if (!clusterIds) return [];
    return clusterIds
      .map((id) => items.find((i) => i.id === id))
      .filter((i): i is DiscoverItem => i != null);
  }, [clusterIds, items]);

  const closeCards = () => {
    setFloatingId(null);
    setClusterIds(null);
  };
  // A pin and a bubble answer the same question, so only one of them answers
  // it at a time.
  const selectPin = (id: string) => {
    setClusterIds(null);
    setFloatingId(id);
  };

  // Move keyboard focus into the floating card when it opens so keyboard users
  // land on the primary action (the card is non-modal — the sheet stays usable).
  const viewBtnRef = useRef<HTMLButtonElement | null>(null);
  const focusKey = floatingId ?? (clusterIds ? clusterIds.join(",") : null);
  useEffect(() => {
    if (focusKey) viewBtnRef.current?.focus();
  }, [focusKey]);

  // Changing city answers a different question — drop whatever was open.
  useEffect(() => {
    setFloatingId(null);
    setClusterIds(null);
  }, [activeCitySlug]);

  /**
   * Where each city is, averaged from its own venues rather than read off the
   * city row, whose stored coordinates are not reliable.
   */
  const centroids = useMemo(() => cityCentroids(allItems), [allItems]);
  const centroidsRef = useRef(centroids);
  centroidsRef.current = centroids;
  const activeCitySlugRef = useRef(activeCitySlug);
  activeCitySlugRef.current = activeCitySlug;
  const askedForLocationRef = useRef(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  /**
   * Open on the city the traveller is standing in.
   *
   * The whole-country frame is the answer to "we have no idea", and it was the
   * answer everybody got — someone in Marrakech opened on the Atlantic and had
   * to go and find their own city, every single time. Asking runs after the
   * first paint and the map never waits on it: a refusal, a browser with no
   * geolocation, a fix that lands nowhere near a city we cover all leave the
   * country frame exactly as it was.
   */
  useEffect(() => {
    if (askedForLocationRef.current) return undefined;
    // The traveller has already said where they are going.
    if (activeCitySlug) return undefined;
    // Nothing to match against until the index lands.
    if (allItems.length === 0) return undefined;
    if (typeof navigator === "undefined" || !navigator.geolocation) return undefined;
    // The desktop shell mounts this map too, hidden behind display:none. Nobody
    // is looking at a hidden map, so it is not the thing that gets to ask the
    // browser where the traveller is.
    const root = rootRef.current;
    if (!root || root.offsetWidth === 0) return undefined;
    askedForLocationRef.current = true;
    let cancelled = false;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        if (cancelled) return;
        const slug = nearestCitySlug(
          pos.coords.latitude,
          pos.coords.longitude,
          centroidsRef.current,
        );
        // Never override a city the traveller picked while we were waiting.
        if (!slug || activeCitySlugRef.current) return;
        setCity(slug);
      },
      () => {
        // Denied, indoors, no fix: the country frame is still a fine answer.
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 30 * 60_000 },
    );
    return () => {
      cancelled = true;
    };
  }, [activeCitySlug, allItems.length, setCity]);

  // Re-frame on a city pick or a change of what is plotted — never on a pan or
  // a zoom, which are the traveller driving.
  const fitKey = `${activeCitySlug ?? "all"}|${items.length}|${items[0]?.id ?? ""}`;

  const initialCenterRef = useRef(MOROCCO_CENTER);
  const initialZoomRef = useRef(activeCitySlug ? CITY_ZOOM : COUNTRY_ZOOM);

  if (!API_KEY) {
    return <div className="mj-roamy-backdrop" aria-hidden="true" />;
  }

  const renderCard = (item: DiscoverItem, isFirst: boolean, inRow: boolean) => (
    <div
      key={item.id}
      className="mj-map-floatcard"
      style={{
        ["--cat" as string]: styleForItem(item).color,
        ...(inRow ? CLUSTER_CARD_STYLE : null),
      }}
      role={inRow ? "group" : "dialog"}
      aria-label={item.title}
      dir={i18n.dir()}
      onKeyDown={(e) => {
        if (e.key === "Escape") closeCards();
      }}
    >
      {item.image ? (
        <img
          className="mj-map-floatcard__img"
          src={thumbUrl(item.image, 96)}
          alt={item.imageAlt ?? item.title}
          loading="lazy"
        />
      ) : null}
      <div className="mj-map-floatcard__body">
        <div className="mj-map-floatcard__title">{item.title}</div>
        <div className="mj-map-floatcard__meta">
          {[item.subtitle || item.location, itemPrice(item)].filter(Boolean).join(" · ")}
        </div>
      </div>
      <button
        ref={isFirst ? viewBtnRef : undefined}
        type="button"
        className="mj-map-floatcard__view"
        onClick={() => {
          onSelectItem(item.id);
          closeCards();
        }}
      >
        {t("discover.pin.view_details")}
      </button>
    </div>
  );

  return (
    <div className="mj-roamy-backdrop" aria-hidden={false} ref={rootRef}>
      <APIProvider apiKey={API_KEY} region="MA" language={i18n.language.split("-")[0]}>
        <GoogleMap
          mapId={MAP_ID}
          colorScheme="LIGHT"
          defaultCenter={initialCenterRef.current}
          defaultZoom={initialZoomRef.current}
          gestureHandling="greedy"
          disableDefaultUI
          clickableIcons={false}
          style={{ width: "100%", height: "100%" }}
        >
          {/* Frame the pins themselves, and with a city picked only the ones
              near its core — a fixed zoom on the average of every result put
              Marrakech's camera out in the Agafay desert. */}
          <FitBoundsController
            fitKey={fitKey}
            items={items}
            fallback={MOROCCO_CENTER}
            hasCity={Boolean(activeCitySlug)}
          />
          <MapClickCloser onClose={closeCards} />
          {onBoundsChange ? (
            <BoundsReporter minZoom={VIEWPORT_SYNC_ZOOM} onBounds={onBoundsChange} />
          ) : null}
          <ClusteredPins
            items={items}
            infoItemId={floatingId}
            onSelect={selectPin}
            onClusterMembers={setClusterIds}
            isMobile
          />
        </GoogleMap>
      </APIProvider>
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 bottom-0 h-[18vh] bg-gradient-to-t from-white/85 via-white/30 to-transparent"
      />

      {clusterItems.length > 0
        ? createPortal(
            <div
              className="mj-no-scrollbar"
              style={CLUSTER_ROW_STYLE}
              role="group"
              aria-label={t("discover.pin.cluster_group", {
                defaultValue: "Places at this spot",
              })}
              dir={i18n.dir()}
            >
              {clusterItems.map((item, index) => renderCard(item, index === 0, true))}
            </div>,
            document.body,
          )
        : floatingItem
          ? createPortal(renderCard(floatingItem, true, false), document.body)
          : null}
    </div>
  );
}
